import * as React from "react";
import {
  Accordion,
  Autocomplete,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
  TextField,
  Select,
  MenuItem,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import AstroChart from "./AstroChart";
import LoadingButton from "@mui/lab/LoadingButton";
import timeZones from "./data/timeZone";
import standardTimeZones from "./data/standardTimeZone";
import { convertToISO8601 } from "./Utils/ConvertDateTimeToISO";
import { extractUtcOffset } from "./Utils/extractUTCoffSet";
import { convertToAMPM } from "./Utils/convertAMPM";
import { divisionalChartGenerator } from "./Utils/divisionalChartGenerator";
import { getPositionOfAscendantInDivisionalChart } from "./Utils/getAscPositionDivChart";
import { chartDNumbers, chartOptions, chartNames } from "./data/chartOptions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Fab from "@mui/material/Fab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-regular-svg-icons";
import SendIcon from "@mui/icons-material/Send";
import {
  interactiveCalculatorInputBoxColor,
  globalInputField,
  settingsInInteractiveCalculator,
  settingsAccordionDetails,
} from "./constants";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";

function Calculator() {
  const [dateOfBirth, setDateOfBirth] = React.useState(dayjs());
  const [placeOfBirthInput, setPlaceOfBirthInput] = React.useState(""); // Separate state for input
  const [timeOfBirth, setTimeOfBirth] = React.useState(dayjs());
  const [locationSuggestions, setLocationSuggestions] = React.useState([]);
  const [name, setName] = React.useState(" ");
  const [selectedGender, setSelectedGender] = React.useState("Female");
  const [latitude, setLatitude] = React.useState("");
  const [longitude, setLongitude] = React.useState("");
  const [timeZone, setTimeZone] = React.useState(""); // New state variable for TimeZone
  const [countryShortCode, setCountryShortCode] = React.useState("");
  const [selectedAyanamsa, setSelectedAyanamsa] = React.useState(1);
  const [selectedLanguage, setSelectedLanguage] = React.useState("en");
  const [selectedHouseSystem, setSelectedHouseSystem] = React.useState("eh");
  const [selectedStyle, setSelectedStyle] = React.useState("south-indian");
  const [responseD1, setResponseD1] = React.useState("");
  const [responseX1, setResponseX1] = React.useState("");
  const [responseX2, setResponseX2] = React.useState("");
  const [responseX3, setResponseX3] = React.useState("");
  const [chartOption1, setChartOption1] = React.useState("lagna");
  const [chartOption2, setChartOption2] = React.useState("navamsa");
  const [chartOption3, setChartOption3] = React.useState("dasamsa");
  const [showBackButton, setShowBackButton] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isPDFButtonLoading, setIsPDFButtonLoading] = React.useState(false);
  const [clickCount, setClickCount] = React.useState(0);
  const [triggerOpenCage, setTriggerOpenCage] = React.useState(false);
  const maxClicks = 0;

  React.useEffect(() => {
    console.log(chartOption1, chartOption2, chartOption3);
    console.log(responseX2);
    console.log(responseX3);
    console.log(responseX1);
  }, [
    chartOption1,
    chartOption2,
    chartOption3,
    responseX1,
    responseX2,
    responseX3,
  ]);

  const [svgViewBoxWidth, setSvgViewBoxWidth] = React.useState(500);
  const [svgSquareSideLength, setSvgSquareSideLength] = React.useState(500);
  const [scaleDownFactor, setScaleDownFactor] = React.useState(1);

  React.useEffect(() => {
    const updateSvgAttributes = () => {
      const viewportWidth = window.innerWidth;
      let newSvgViewBoxWidth = 500;
      let newSvgSquareSideLength = 500;
      let scaleDownFactorX = 1;

      if (viewportWidth > 1810) {
        newSvgViewBoxWidth = viewportWidth * 0.287;
        newSvgSquareSideLength = newSvgViewBoxWidth;
        scaleDownFactorX = (viewportWidth * 0.287) / 500;
      } else if (viewportWidth >= 900 && viewportWidth < 1800) {
        newSvgViewBoxWidth = (viewportWidth - 300) / 3;
        newSvgSquareSideLength = newSvgViewBoxWidth;
        scaleDownFactorX = (viewportWidth - 300) / 1500;
      } else if (viewportWidth >= 600 && viewportWidth < 900) {
        newSvgViewBoxWidth = viewportWidth * 0.3644;
        newSvgSquareSideLength = newSvgViewBoxWidth;
        scaleDownFactorX = (viewportWidth * 0.3644) / 500;
      } else if (viewportWidth < 600) {
        newSvgViewBoxWidth = viewportWidth * 0.78;
        newSvgSquareSideLength = newSvgViewBoxWidth;
        scaleDownFactorX = (viewportWidth * 0.78) / 500;
      }

      setSvgViewBoxWidth(newSvgViewBoxWidth);
      setSvgSquareSideLength(newSvgSquareSideLength);
      setScaleDownFactor(scaleDownFactorX);
    };

    // Call the function initially
    updateSvgAttributes();

    // Add event listener to update on window resize
    window.addEventListener("resize", updateSvgAttributes);

    // Remove event listener on component unmount
    return () => {
      window.removeEventListener("resize", updateSvgAttributes);
    };
  }, []); // Empty dependency array ensures this effect runs only once on mount

  const handlePlaceOfBirthSelect = (event, value, reason) => {
    if (reason === "selectOption") {
      const selectedPlace = locationSuggestions.find(
        (place) => place.place_name === value
      );
      if (selectedPlace) {
        fetchLocationDetails(selectedPlace);
      }
    }

    if (reason === "clear") {
      setLatitude("");
      setLongitude("");
      setTimeZone("");
      setCountryShortCode("");
    }
  };

  const isSubmitDisabled = () => {
    return !dateOfBirth || !timeOfBirth || !latitude || !longitude || !name;
  };

  // throw this OUT
  const extractRetrogradeStatus = (planetPositionAPIData) => {
    const retrogradePlanets = {};
    planetPositionAPIData.planet_position.forEach((planet) => {
      retrogradePlanets[planet.id] = planet.is_retrograde;
    });
    return retrogradePlanets;
  };

  const handleSubmit = async () => {
    // setClickCount(0)
    setIsLoading(true);
    try {
      const requests = [
        axios.get(
          `https://astrodata.appili.workers.dev/v2/astrology/planet-position?ayanamsa=${selectedAyanamsa}&coordinates=${latitude},${longitude}&datetime=${convertToISO8601(
            dateOfBirth,
            timeOfBirth,
            extractUtcOffset(timeZone)
          )}&chart_type=rasi&chart_style=${selectedStyle}`
        ),
        axios.get(
          `https://astrodata.appili.workers.dev/v2/astrology/divisional-planet-position?ayanamsa=${selectedAyanamsa}&coordinates=${latitude},${longitude}&datetime=${convertToISO8601(
            dateOfBirth,
            timeOfBirth,
            extractUtcOffset(timeZone)
          )}&chart_type=${chartOption1}`
        ),
        axios.get(
          `https://astrodata.appili.workers.dev/v2/astrology/divisional-planet-position?ayanamsa=${selectedAyanamsa}&coordinates=${latitude},${longitude}&datetime=${convertToISO8601(
            dateOfBirth,
            timeOfBirth,
            extractUtcOffset(timeZone)
          )}&chart_type=${chartOption2}`
        ),
        axios.get(
          `https://astrodata.appili.workers.dev/v2/astrology/divisional-planet-position?ayanamsa=${selectedAyanamsa}&coordinates=${latitude},${longitude}&datetime=${convertToISO8601(
            dateOfBirth,
            timeOfBirth,
            extractUtcOffset(timeZone)
          )}&chart_type=${chartOption3}`
        ),
      ];

      const responses = await Promise.all(requests);
      responses.forEach((response, index) => {
        switch (index) {
          case 0:
            setResponseD1(response.data.data);
            break;
          case 1:
            setResponseX1(response.data.data);
            break;
          case 2:
            setResponseX2(response.data.data);
            break;
          case 3:
            setResponseX3(response.data.data);
            break;
          default:
            break;
        }
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setShowBackButton(true);
    setIsLoading(false);
    localStorage.removeItem("locationGeo");
  };

  React.useEffect(() => {
    fetchLocation();
  }, []); // Run fetchLocation once on component mount

  const fetchLocation = async () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          setTriggerOpenCage(!triggerOpenCage);

          // Save to local storage
          localStorage.setItem("latitudeGeo", position.coords.latitude);
          localStorage.setItem("longitudeGeo", position.coords.longitude);
        },
        (error) => {
          console.error("Error getting location:", error.message);
          setLatitude(17.4065);
          setLongitude(78.4772);
          setTriggerOpenCage(!triggerOpenCage);

          // Save default coordinates to local storage
          localStorage.setItem("latitudeGeo", 17.4065);
          localStorage.setItem("longitudeGeo", 78.4772);
        }
      );
    } else {
      console.error("Geolocation is not supported.");
    }
  };

  const checkLocalStorage = async (itemKey) => {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        const value = localStorage.getItem(itemKey);
        if (value) {
          localStorage.removeItem(itemKey);
          clearInterval(interval);
          resolve(value);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(interval);
      }, 1000);
    });
  };

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      if (latitude && longitude) {
        clearInterval(intervalId); // Stop the interval once coordinates are available
        getLocationName(); // Call getLocationName once coordinates are available
      }
    }, 100); // Check every 100 milliseconds

    // Cleanup function to clear interval if component unmounts or dependencies change
    return () => clearInterval(intervalId);
  }, [triggerOpenCage]); // Depend on latitude and longitude changes

  React.useEffect(() => {
    const fetchDefaultLocation = async () => {
      const value = await checkLocalStorage("locationGeo");
      setPlaceOfBirthInput(value);
      const value2 = await checkLocalStorage("locationGeoTimeZone");
      setTimeZone(value2);
    };
    fetchDefaultLocation();
  }, []);

  const getLocationName = async () => {
    if (!localStorage.getItem("locationGeo") && latitude && longitude) {
      // Fetch city name and timezone using OpenCage Geocoder
      const apiKey = "7768d1134ba34e0fa88d3be8e0ca6547"; // Replace with your actual API key
      const url = `https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=${apiKey}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error("Failed to fetch location data");
        }
        const data = await response.json();
        const city = data.results[0].components.city;
        const timezone = data.results[0].annotations.timezone.short_name;

        console.log("countryTimeZoneCode", timezone);
        console.log("City:", city);
        console.log("Timezone:", standardTimeZones[timezone]);

        localStorage.setItem("locationGeo", city);
        localStorage.setItem(
          "locationGeoTimeZone",
          `${timezone} ${standardTimeZones[timezone]}`
        );
      } catch (error) {
        console.error("Error fetching location:", error.message);
      }
    }
  };

  const fetchLocationSuggestions = async (input) => {
    const apiKey =
      "pk.eyJ1IjoiYXBwaWxpdmFtc2lrcmlzaG5hIiwiYSI6ImNsdWlqcDdtNDAzajAya2xpY2pxYzV0OXAifQ.Dy_O2ndNB53xXFj0ZzaIag"; // Replace with your Mapbox API key
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      input
    )}.json?types=place&access_token=${apiKey}`;

    try {
      const response = await axios.get(url);
      setLocationSuggestions(response.data.features.slice(0, 6));
    } catch (error) {
      console.error("Error fetching location suggestions:", error);
    }
  };

  const genderOptions = ["Male", "Female", "Other"];

  const fetchLocationDetails = async (place) => {
    const lat = place.center[1];
    const lng = place.center[0];

    let countryShortCode = "";
    const countryContext = place.context.find((item) =>
      item.id.startsWith("country.")
    );
    if (countryContext) {
      countryShortCode = countryContext.short_code || "";
    }

    // Automatically select the first TimeZone option for the selected country
    if (
      timeZones[countryShortCode.toUpperCase()] &&
      timeZones[countryShortCode.toUpperCase()].length === 1
    ) {
      setTimeZone(timeZones[countryShortCode.toUpperCase()][0]);
    }

    setLatitude(lat);
    setLongitude(lng);
    setCountryShortCode(countryShortCode);
  };

  const handlePDFClick = async () => {
    setIsPDFButtonLoading(true);
    try {
      const response = await axios.get(
        `https://astrodata.appili.workers.dev/v2/report/personal-reading/instant?input[first_name]=${name}&input[middle_name]=${""}&input[last_name]=${"."}&input[gender]=${selectedGender.toLowerCase()}&input[datetime]=${convertToISO8601(
          dateOfBirth,
          timeOfBirth,
          extractUtcOffset(timeZone)
        )}&input[coordinates]=${latitude},${longitude}&input[place]=${placeOfBirthInput.substring(
          0,
          17
        )}&options[template][style]=basic&options[template][footer]=<a href="https://www.astrodata.app/">astrodata.app</a>  |  <a href="https://astrodata.app/">astrodata.app</a>   | 📧 info@astrodata.app&options[report][name]=Astro Data&options[report][caption]=Horoscope&options[report][brand_name]=Astro Data&options[modules][0][name]=birth-details&options[modules][1][name]=chart&options[modules][1][options][chart_style]=south-indian&options[modules][2][name]=bhava-chart&options[modules][2][options][chart_style]=south-indian&options[modules][3][name]=planet-position&options[modules][4][name]=dasa-periods&options[modules][4][options][antardasha]=all&options[modules][4][options][pratyantardasha]=all&options[modules][5][name]=sarvashtakavarga-chart&options[modules][5][options][chart_style]=south-indian&options[modules][5][options][planet_ashtakavarga]=all&options[modules][6][name]=shadbala-table&options[modules][7][name]=sade-sati&options[modules][7][options][chart_style]=south-india`,
        {
          responseType: "blob", // Important for downloading binary data
        }
      );

      // Create a URL for the PDF blob
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfURL = URL.createObjectURL(pdfBlob);

      // Create a link element and simulate a click to download the PDF
      const link = document.createElement("a");
      link.href = pdfURL;
      link.setAttribute("download", `${name}-astrodata.pdf`); // Set the name of the downloaded file
      document.body.appendChild(link);
      link.click();

      // Clean up by removing the link element and revoking the object URL
      link.remove();
      URL.revokeObjectURL(pdfURL);

      // Increment the click count
      setClickCount((prevCount) => prevCount + 1);
    } catch (error) {
      console.error("Error downloading the PDF:", error);
    }

    setIsPDFButtonLoading(false);
  };

  return (
    <div>
      {showBackButton ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <Fab
              color="secondary"
              aria-label="edit"
              onClick={() => setShowBackButton(false)}
            >
              <ArrowBackIcon />
            </Fab>
            <Box
              sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}
            >
              <LoadingButton
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                onClick={handlePDFClick}
                loading={isPDFButtonLoading}
                disabled={clickCount >= maxClicks || isPDFButtonLoading}
                loadingPosition="start"
              >
                {clickCount >= maxClicks
                  ? `Download Limit Reached (${maxClicks} times)`
                  : "PDF Report"}
              </LoadingButton>
            </Box>
          </Box>

          {responseD1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center", // Center items horizontally
                alignItems: "center", // Center items vertically
                flexWrap: "wrap",
                "& > :not(style)": {
                  marginBottom: "15px",
                  width: "96%", // Set width to 95% of horizontal space
                  height: "auto", // Allow height to be determined by content
                },
              }}
            >
              <Grid container spacing={3}>
                {/* Apply spacing directly to the Grid container */}
                <Grid item xs={12} md={4} sm={6}>
                  <Grid item>
                    <Paper
                      elevation={6}
                      sx={{ padding: "10px", marginBottom: "10px" }}
                    >
                      <Autocomplete
                        id="combo-box-demo"
                        options={chartOptions}
                        getOptionLabel={(option) => option.label}
                        onChange={async (event, newValue) => {
                          setChartOption1(newValue ? newValue.value : null);

                          try {
                            const response = await axios.get(
                              `https://astrodata.appili.workers.dev/v2/astrology/divisional-planet-position?ayanamsa=${selectedAyanamsa}&coordinates=${latitude},${longitude}&datetime=${convertToISO8601(
                                dateOfBirth,
                                timeOfBirth,
                                extractUtcOffset(timeZone)
                              )}&chart_type=${
                                newValue.value
                              }&la=${selectedLanguage}`
                            );
                            setResponseX1(response.data.data);
                          } catch (error) {
                            console.error("Error fetching data:", error);
                          }
                        }}
                        value={
                          chartOptions.find(
                            (option) => option.value === chartOption1
                          ) || null
                        } // Find the option object corresponding to chartOption2
                        renderInput={(params) => (
                          <TextField {...params} label="Select Chart" />
                        )}
                      />
                    </Paper>
                  </Grid>
                  <Grid item>
                    <Paper elevation={6} sx={{ padding: "10px" }}>
                      {responseD1 && responseX1 && (
                        <AstroChart
                          name={name}
                          gender={selectedGender}
                          chartName={chartNames[chartOption1]}
                          chartCode={chartDNumbers[chartOption1]}
                          date={dateOfBirth}
                          place={placeOfBirthInput}
                          time={convertToAMPM(timeOfBirth)}
                          // asc={getPositionOfAscendant(responseD1.planet_position)}
                          asc={getPositionOfAscendantInDivisionalChart(
                            responseX1.divisional_positions
                          )}
                          timeZone={timeZone.substring(0, 5)}
                          planets={divisionalChartGenerator(
                            responseX1,
                            extractRetrogradeStatus(responseD1)
                          )}
                          // planets={chartGenerator(responseD1)}
                          svgViewBox={`0 0 ${svgViewBoxWidth} ${svgViewBoxWidth}`}
                          svgSquareSideLength={svgSquareSideLength}
                          scale={scaleDownFactor}
                        />
                      )}
                    </Paper>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4} sm={6}>
                  <Grid item>
                    <Paper
                      elevation={6}
                      sx={{ padding: "10px", marginBottom: "10px" }}
                    >
                      <Autocomplete
                        id="combo-box-demo"
                        options={chartOptions}
                        getOptionLabel={(option) => option.label}
                        onChange={async (event, newValue) => {
                          setChartOption2(newValue ? newValue.value : null);

                          try {
                            const response = await axios.get(
                              `https://astrodata.appili.workers.dev/v2/astrology/divisional-planet-position?ayanamsa=${selectedAyanamsa}&coordinates=${latitude},${longitude}&datetime=${convertToISO8601(
                                dateOfBirth,
                                timeOfBirth,
                                extractUtcOffset(timeZone)
                              )}&chart_type=${
                                newValue.value
                              }&la=${selectedLanguage}`
                            );
                            setResponseX2(response.data.data);
                          } catch (error) {
                            console.error("Error fetching data:", error);
                          }
                        }}
                        value={
                          chartOptions.find(
                            (option) => option.value === chartOption2
                          ) || null
                        } // Find the option object corresponding to chartOption2
                        renderInput={(params) => (
                          <TextField {...params} label="Select Chart" />
                        )}
                      />
                    </Paper>
                  </Grid>
                  <Grid item>
                    <Paper elevation={6} sx={{ padding: "10px" }}>
                      {responseD1 && responseX2 && (
                        <AstroChart
                          name={name}
                          gender={selectedGender}
                          chartName={chartNames[chartOption2]}
                          chartCode={chartDNumbers[chartOption2]}
                          date={dateOfBirth}
                          place={placeOfBirthInput}
                          time={convertToAMPM(timeOfBirth)}
                          asc={getPositionOfAscendantInDivisionalChart(
                            responseX2.divisional_positions
                          )}
                          timeZone={timeZone.substring(0, 5)}
                          planets={divisionalChartGenerator(
                            responseX2,
                            extractRetrogradeStatus(responseD1)
                          )}
                          svgViewBox={`0 0 ${svgViewBoxWidth} ${svgViewBoxWidth}`}
                          svgSquareSideLength={svgSquareSideLength}
                          scale={scaleDownFactor}
                        />
                      )}
                    </Paper>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4} sm={6}>
                  <Grid item>
                    <Paper
                      elevation={6}
                      sx={{ padding: "10px", marginBottom: "10px" }}
                    >
                      <Autocomplete
                        id="combo-box-demo"
                        options={chartOptions}
                        getOptionLabel={(option) => option.label}
                        onChange={async (event, newValue) => {
                          setChartOption3(newValue ? newValue.value : null);

                          try {
                            const response = await axios.get(
                              `https://astrodata.appili.workers.dev/v2/astrology/divisional-planet-position?ayanamsa=${selectedAyanamsa}&coordinates=${latitude},${longitude}&datetime=${convertToISO8601(
                                dateOfBirth,
                                timeOfBirth,
                                extractUtcOffset(timeZone)
                              )}&chart_type=${
                                newValue.value
                              }&la=${selectedLanguage}`
                            );
                            setResponseX3(response.data.data);
                          } catch (error) {
                            console.error("Error fetching data:", error);
                          }
                        }}
                        value={
                          chartOptions.find(
                            (option) => option.value === chartOption3
                          ) || null
                        } // Find the option object corresponding to chartOption2
                        renderInput={(params) => (
                          <TextField {...params} label="Select Chart" />
                        )}
                      />
                    </Paper>
                  </Grid>
                  <Grid item>
                    <Paper elevation={6} sx={{ padding: "10px" }}>
                      {responseX3 && responseD1 && (
                        <AstroChart
                          name={name}
                          gender={selectedGender}
                          chartName={chartNames[chartOption3]}
                          chartCode={chartDNumbers[chartOption3]}
                          date={dateOfBirth}
                          place={placeOfBirthInput}
                          time={convertToAMPM(timeOfBirth)}
                          asc={getPositionOfAscendantInDivisionalChart(
                            responseX3.divisional_positions
                          )}
                          timeZone={timeZone.substring(0, 5)}
                          planets={divisionalChartGenerator(
                            responseX3,
                            extractRetrogradeStatus(responseD1)
                          )}
                          svgViewBox={`0 0 ${svgViewBoxWidth} ${svgViewBoxWidth}`}
                          svgSquareSideLength={svgSquareSideLength}
                          scale={scaleDownFactor}
                        />
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            marginTop: { xs: "1rem", sm: "1.5rem", md: "2rem" },
            marginBottom: "1%",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
            "& > :not(style)": {
              m: 1,
              minWidth: 128,
              minHeight: 128,
              width: "100%",
              height: "auto",
            },
            "@media (min-width:600px)": {
              "& > :not(style)": {
                width: "calc(75% - 2px)",
                height: "auto",
              },
            },
            "@media (min-width:960px)": {
              "& > :not(style)": {
                width: "calc(65% - 2px)",
                height: "auto",
              },
            },
          }}
        >
          <Paper
            elevation={5}
            sx={{
              paddingTop: 2,
              paddingBottom: 2,
              paddingLeft: 3,
              paddingRight: 3,
              backgroundColor: interactiveCalculatorInputBoxColor,
              borderRadius: 4,
            }}
          >
            <Grid
              container
              spacing={3}
              alignItems="center"
              style={{ marginTop: 1 }}
            >
              <Grid item xs={3} md={3}>
                <Grid container justifyContent="center">
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1">Place of Birth</Typography>
                    <Typography variant="body1" color="error" ml={0.5}>
                      *
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Grid item xs={9} md={9}>
                <Autocomplete
                  sx={{ backgroundColor: globalInputField }}
                  value={placeOfBirthInput}
                  freeSolo={false}
                  options={locationSuggestions.map(
                    (option) => option.place_name
                  )}
                  onChange={handlePlaceOfBirthSelect}
                  inputValue={placeOfBirthInput}
                  onInputChange={(event, newInputValue) => {
                    setPlaceOfBirthInput(newInputValue);
                    fetchLocationSuggestions(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} type="text" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={3} md={3}>
                <Grid container justifyContent="center">
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1"> Date of Birth</Typography>
                    <Typography variant="body1" color="error" ml={0.5}>
                      *
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Grid item md={3} xs={9}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={dateOfBirth}
                    onChange={(newValue) => {
                      setDateOfBirth(newValue);
                    }}
                    fullWidth
                    sx={{ backgroundColor: globalInputField }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={3} md={3}>
                <Grid container justifyContent="center">
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1"> Time of Birth</Typography>
                    <Typography variant="body1" color="error" ml={0.5}>
                      *
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Grid item md={3} xs={9}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    value={timeOfBirth}
                    onChange={(newValue) => {
                      setTimeOfBirth(newValue);
                    }}
                    fullWidth
                    sx={{ backgroundColor: globalInputField }}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={3} md={3}>
                <Grid container justifyContent="center">
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1">Name</Typography>
                    <Typography variant="body1" color="error" ml={0.5}>
                      *
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Grid item xs={9} md={6}>
                <TextField
                  size="small"
                  id="outlined-basic"
                  type="text"
                  onChange={(event) => {
                    setName(event.target.value);
                  }}
                  variant="outlined"
                  fullWidth
                  sx={{ backgroundColor: globalInputField }}
                />
              </Grid>

              <Grid item md={3} xs={12}>
                <Autocomplete
                  size="small"
                  id="combo-box-demo"
                  options={genderOptions}
                  onChange={(event, newValue) => {
                    setSelectedGender(newValue);
                  }}
                  value={selectedGender} // Set the value of the Autocomplete
                  renderInput={(params) => (
                    <TextField {...params} label="Gender" />
                  )}
                  sx={{ backgroundColor: globalInputField }}
                />
              </Grid>

              <Grid item md={12} xs={12}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: settingsInInteractiveCalculator }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ fontWeight: "900" }}>
                        Settings
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ backgroundColor: settingsAccordionDetails, pb: 1 }}
                  >
                    <Grid
                      container
                      alignItems="center"
                      style={{ marginTop: 4 }}
                    >
                      <Grid item xs={3} md={3}>
                        <Grid container justifyContent="center">
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              {" "}
                              Latitude (° N)
                            </Typography>
                            <Typography variant="body1" color="error" ml={0.5}>
                              *
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Grid item md={3} xs={9}>
                        <TextField
                          //  margin="normal"
                          size="small"
                          type="text"
                          value={latitude}
                          onChange={(event) => {
                            setLatitude(event.target.value);
                          }}
                          fullWidth
                          sx={{ backgroundColor: globalInputField, mb: 1 }}
                        />
                      </Grid>

                      <Grid item xs={3} md={3}>
                        <Grid container justifyContent="center">
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              {" "}
                              Longitude (° E)
                            </Typography>
                            <Typography variant="body1" color="error" ml={0.5}>
                              *
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Grid item md={3} xs={9}>
                        <TextField
                          size="small"
                          type="text"
                          value={longitude}
                          onChange={(event) => {
                            setLongitude(event.target.value);
                          }}
                          fullWidth
                          sx={{ backgroundColor: globalInputField, mb: 1 }}
                        />
                      </Grid>

                      <Grid item xs={3} md={3}>
                        <Grid container justifyContent="center">
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              Time Zone
                            </Typography>
                            <Typography variant="body1" color="error" ml={0.5}>
                              *
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Grid item md={3} xs={9}>
                        <Select
                          value={timeZone}
                          size="small"
                          onChange={(event) => {
                            setTimeZone(event.target.value);
                          }}
                          fullWidth
                          sx={{
                            backgroundColor: globalInputField,
                            mb: 1,
                          }}
                        >
                          {timeZones[countryShortCode.toUpperCase()] ? (
                            timeZones[countryShortCode.toUpperCase()].map(
                              (zone) => (
                                <MenuItem key={zone} value={zone}>
                                  {zone}
                                </MenuItem>
                              )
                            )
                          ) : (
                            <MenuItem value={timeZone}>{timeZone}</MenuItem>
                          )}
                        </Select>
                      </Grid>

                      <Grid item xs={3} md={3}>
                        <Grid container justifyContent="center">
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              Chart Style
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Grid item md={3} xs={9}>
                        <Select
                          size="small"
                          value={selectedStyle}
                          onChange={(event) => {
                            setSelectedStyle(event.target.value);
                          }}
                          fullWidth
                          sx={{ backgroundColor: globalInputField, mb: 1 }}
                        >
                          <MenuItem value={"south-indian"}>
                            South Indian
                          </MenuItem>
                          <MenuItem disabled value={"north-indian"}>
                            North Indian
                          </MenuItem>
                          <MenuItem disabled value={"east-indian"}>
                            East Indian
                          </MenuItem>
                        </Select>
                      </Grid>

                      <Grid item xs={3} md={3}>
                        <Grid container justifyContent="center">
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              Ayanamsa
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Grid item md={3} xs={9}>
                        <Select
                          size="small"
                          value={selectedAyanamsa}
                          onChange={(event) => {
                            setSelectedAyanamsa(event.target.value);
                          }}
                          fullWidth
                          sx={{ backgroundColor: globalInputField, mb: 1 }}
                        >
                          <MenuItem value={1}>Lahiri</MenuItem>
                          <MenuItem value={3} disabled>
                            Raman
                          </MenuItem>
                          <MenuItem value={5} disabled>
                            KP astrology
                          </MenuItem>
                        </Select>
                      </Grid>

                      <Grid item xs={3} md={3}>
                        <Grid container justifyContent="center">
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              House System
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Grid item md={3} xs={9}>
                        <Select
                          size="small"
                          value={selectedHouseSystem}
                          onChange={(event) => {
                            setSelectedHouseSystem(event.target.value);
                          }}
                          fullWidth
                          sx={{ backgroundColor: globalInputField, mb: 1 }}
                        >
                          <MenuItem value={"eh"}>Equal House</MenuItem>
                          <MenuItem disabled value={"ueh"}>
                            Unequal House
                          </MenuItem>
                        </Select>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
            <Grid container alignItems="center" sx={{ mt: 1 }}>
              {" "}
              {/* Submit Button */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: { xs: 2, sm: 3, md: 3 }, // Responsive marginTop
                    mb: { xs: 1, sm: 2, md: 2 }, // Responsive marginBottom
                    mr: { xs: 1, sm: 2, md: 3 }, // Responsive marginRight
                  }}
                >
                  <Tooltip
                    title={
                      isSubmitDisabled()
                        ? "Fill all required details to enable submit button"
                        : ""
                    }
                  >
                    <span>
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        loading={isLoading}
                        disabled={isSubmitDisabled() || isLoading} // Disable the button when loading
                        endIcon={<SendIcon />}
                        loadingPosition="end"
                      >
                        Submit
                      </LoadingButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}
    </div>
  );
}

export default Calculator;
